import type { ApiClient, IApiJsonRequest } from '../../ApiClient';
import type {
  IApiAddAppUserRequestBody,
  IApiCreateAppRequestBody,
  IApiDeleteAppResponseBody,
  IApiDownloadBundleQueryStringParams,
  IApiDownloadBundleResponseBody,
  IApiDownloadScreenshotsResponseBody,
  IApiGetAppResponseBody,
  IApiGetAppsResponseBody,
  IApiGetAppUserResponseBody,
  IApiGetAppUsersResponseBody,
  IApiGetCheckoutUrlRequestBody,
  IApiGetCheckoutUrlResponseBody,
  IApiGetCurrentFeaturePlanResponseBody,
  IApiRemoveAppUserResponseBody,
  IApiUpdateAppRequestBody,
  IApiUpdateAppUserRequestBody,
} from './AppsApi.types';

export class AppsApi {
  public constructor(private client: ApiClient) {}

  public async create(
    body: IApiCreateAppRequestBody,
  ): Promise<IApiGetAppResponseBody> {
    const request: IApiJsonRequest = {
      method: 'post',
      path: '/apps',
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppResponseBody>(request);

    return data;
  }

  public async getAll(): Promise<IApiGetAppsResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: '/apps',
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppsResponseBody>(request);

    return data;
  }

  public async get(appId: string): Promise<IApiGetAppResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/apps/${appId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppResponseBody>(request);

    return data;
  }

  public async update(
    appId: string,
    body: IApiUpdateAppRequestBody,
  ): Promise<IApiGetAppResponseBody> {
    const request: IApiJsonRequest = {
      method: 'patch',
      path: `/apps/${appId}`,
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppResponseBody>(request);

    return data;
  }

  public async delete(appId: string): Promise<IApiDeleteAppResponseBody> {
    const request: IApiJsonRequest = {
      method: 'delete',
      path: `/apps/${appId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiDeleteAppResponseBody>(
        request,
      );

    return data;
  }

  public async getUsers(appId: string): Promise<IApiGetAppUsersResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/apps/${appId}/users`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppUsersResponseBody>(
        request,
      );

    return data;
  }

  public async getUser(
    appId: string,
    userId: string,
  ): Promise<IApiGetAppUserResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/apps/${appId}/users/${userId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppUserResponseBody>(
        request,
      );

    return data;
  }

  public async addUser(
    appId: string,
    body: IApiAddAppUserRequestBody,
  ): Promise<IApiGetAppUserResponseBody> {
    const request: IApiJsonRequest = {
      method: 'put',
      path: `/apps/${appId}/users`,
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppUserResponseBody>(
        request,
      );

    return data;
  }

  public async updateUser(
    appId: string,
    userId: string,
    body: IApiUpdateAppUserRequestBody,
  ): Promise<IApiGetAppUserResponseBody> {
    const request: IApiJsonRequest = {
      method: 'patch',
      path: `/apps/${appId}/users/${userId}`,
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetAppUserResponseBody>(
        request,
      );

    return data;
  }

  public async removeUser(
    appId: string,
    userId: string,
  ): Promise<IApiRemoveAppUserResponseBody> {
    const request: IApiJsonRequest = {
      method: 'delete',
      path: `/apps/${appId}/users/${userId}`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiRemoveAppUserResponseBody>(
        request,
      );

    return data;
  }

  public async downloadBundle(
    appId: string,
    params: IApiDownloadBundleQueryStringParams,
  ): Promise<IApiDownloadBundleResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/apps/${appId}/downloadBundle`,
      params,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiDownloadBundleResponseBody>(
        request,
      );

    return data;
  }

  public async downloadScreenshots(
    appId: string,
  ): Promise<IApiDownloadScreenshotsResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/apps/${appId}/downloadScreenshots`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiDownloadScreenshotsResponseBody>(
        request,
      );

    return data;
  }

  public async getCurrentFeaturePlan(
    appId: string,
  ): Promise<IApiGetCurrentFeaturePlanResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/apps/${appId}/plans/current`,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetCurrentFeaturePlanResponseBody>(
        request,
      );

    return data;
  }

  public async getCheckout(
    appId: string,
    body: IApiGetCheckoutUrlRequestBody,
  ): Promise<IApiGetCheckoutUrlResponseBody> {
    const request: IApiJsonRequest = {
      method: 'post',
      path: `/apps/${appId}/checkout`,
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetCheckoutUrlResponseBody>(
        request,
      );

    return data;
  }
}
